import { addDuration, dateInputToUTCDateTime } from "@/services/UtilitiesService";

/** Throws if `newDuration` violates the boundary in the given direction; a no-op otherwise. */
export function assertRetentionBoundary(newDuration: Date, against: Date, condition: "lesser" | "greater"): void {
  if (
    (condition === "lesser" && newDuration.getTime() >= against.getTime()) ||
    (condition === "greater" && newDuration.getTime() <= against.getTime())
  )
    return;

  throw new Error(
    `The retention duration cannot be ${condition === "lesser" ? "before" : "after"} ${against.toLocaleString(undefined, { dateStyle: "medium" })}.`,
  );
}

/**
 * Validates a proposed retention expiry against the brand's retention rules:
 * - Can't reduce below the current expiry, unless `canReduce`.
 * - Can't reduce below `minExtensionDuration` (the brand's default/minimum), unless `canReduceBelowMin`.
 * - Can't extend beyond `maxExtensionDuration` beyond the later of the brand's minimum retention expiry and the end of the current day.
 *
 * Boundary values themselves are always allowed — only strictly exceeding the boundary throws.
 */
export function assertRetentionDurationWithinBounds(
  proposedExpiry: Date,
  args: {
    startDate: Date;
    currentDuration: string | null | undefined;
    //
    minExtensionDuration: string;
    maxExtensionDuration: string | null | undefined;
    //
    canReduce: boolean;
    canReduceBelowMin: boolean;
  },
) {
  if (!args.canReduceBelowMin) {
    const minRetentionExpiry = addDuration(args.startDate, args.minExtensionDuration);
    assertRetentionBoundary(proposedExpiry, minRetentionExpiry, "lesser");
  }

  if (args.currentDuration != null) {
    const currentRetentionExpiry = addDuration(args.startDate, args.currentDuration);
    if (!args.canReduce) assertRetentionBoundary(proposedExpiry, currentRetentionExpiry, "lesser");
  }

  if (args.maxExtensionDuration != null) {
    const maxRetentionExpiry = addDuration(
      new Date(
        Math.max(
          addDuration(args.startDate, args.minExtensionDuration).getTime(),
          dateInputToUTCDateTime(new Date().toISOString(), true).getTime(),
        ),
      ),
      args.maxExtensionDuration,
    );
    assertRetentionBoundary(proposedExpiry, maxRetentionExpiry, "greater");
  }
}
